// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Live putt estimation for the putt test.
 *
 * The detector proposes, the human judges: every genuine audio signal or
 * wrist peak becomes (or joins) a putt CANDIDATE, shown the moment it
 * happens, and the developer marks each one correct or wrong — plus "missed"
 * when a real putt produced no candidate at all. That yields precision
 * (correct / judged) and recall (correct / (correct + missed)) directly from
 * a green session, which is exactly the evidence a real combined putt
 * detector needs before it is worth building.
 *
 * Combination rule: signals from the two sensors within [COMBINE_MS] of a
 * candidate's latest signal merge into that candidate (a putt's click and its
 * wrist bump are near-simultaneous); a candidate closes once
 * [CANDIDATE_REFRACTORY_MS] passes without new signals, and the next signal
 * after that opens a new one.
 *
 * The judging buttons are themselves an instrument problem — a finger
 * striking the screen is a sound centimetres from the microphone, and a
 * wrist jolt when tapped with the watch hand — so candidates born within
 * [TAP_NOISE_MS] of a button press are flagged and excluded from judgment
 * rather than queueing up as phantom putts.
 */

/** A candidate born this close to a UI button press is the press itself. */
export const TAP_NOISE_MS = 700;

/** Signals this close together are one putt seen by several sensors. */
export const COMBINE_MS = 1500;

/** Quiet time that closes a candidate; putts are further apart than this. */
export const CANDIDATE_REFRACTORY_MS = 2500;

export type PuttSound = { at: number; rise: number; kind: 'impact' | 'nearMiss' };
export type PuttPeak = {
  at: number;
  magnitude: number;
  /** Wrist stillness before the burst, ms, when the watch reported it. */
  stillnessMs?: number;
  /** Peak wrist rotation during the burst, rad/s, on watches with a gyro. */
  rotationRadS?: number;
};

export type PuttCandidate = {
  /**
   * Timestamp of the candidate's FIRST signal. Signals only ever append, so
   * this is stable across rebuilds and safe to key verdicts by.
   */
  at: number;
  /** Which sensors contributed. */
  sources: Array<'audio' | 'wrist'>;
  /** Loudest audio rise in dB, when audio contributed. */
  bestRiseDb: number | null;
  /** Strongest wrist peak in m/s², when the wrist contributed. */
  bestMagnitude: number | null;
  /**
   * Longest reported wrist stillness before the burst, ms — the address
   * signature. Null when no contributing peak carried the measurement.
   */
  stillnessMs: number | null;
  /**
   * Strongest wrist rotation across contributing peaks, rad/s — the swing
   * signature. Null when no contributing peak carried a gyro reading.
   */
  bestRotationRadS: number | null;
  /** Born at a judging-button press — excluded from the verdict queue. */
  tapNoise: boolean;
};

type SensorEvent =
  | { at: number; source: 'audio'; rise: number }
  | {
      at: number;
      source: 'wrist';
      magnitude: number;
      stillnessMs?: number;
      rotationRadS?: number;
    };

export const buildPuttCandidates = (
  sounds: readonly PuttSound[],
  peaks: readonly PuttPeak[],
  uiTapTimes: readonly number[]
): PuttCandidate[] => {
  const events: SensorEvent[] = [
    ...sounds.map((sound): SensorEvent => ({ at: sound.at, source: 'audio', rise: sound.rise })),
    ...peaks.map(
      (peak): SensorEvent => ({
        at: peak.at,
        source: 'wrist',
        magnitude: peak.magnitude,
        stillnessMs: peak.stillnessMs,
        rotationRadS: peak.rotationRadS,
      })
    ),
  ].sort((a, b) => a.at - b.at);

  const candidates: Array<PuttCandidate & { lastSignalAt: number }> = [];

  for (const event of events) {
    const current = candidates[candidates.length - 1];
    // Anything inside the combine-or-refractory window of the previous
    // candidate belongs to it: a second sensor confirming, or an echo of the
    // same stroke. Only after real quiet does a new putt begin.
    const absorb =
      current !== undefined &&
      event.at - current.lastSignalAt <= Math.max(COMBINE_MS, CANDIDATE_REFRACTORY_MS);

    if (absorb) {
      current.lastSignalAt = event.at;
      if (event.source === 'audio') {
        if (!current.sources.includes('audio')) current.sources.push('audio');
        current.bestRiseDb = Math.max(current.bestRiseDb ?? -Infinity, event.rise);
      } else {
        if (!current.sources.includes('wrist')) current.sources.push('wrist');
        current.bestMagnitude = Math.max(current.bestMagnitude ?? -Infinity, event.magnitude);
        if (event.stillnessMs !== undefined) {
          current.stillnessMs = Math.max(current.stillnessMs ?? -Infinity, event.stillnessMs);
        }
        if (event.rotationRadS !== undefined) {
          current.bestRotationRadS = Math.max(
            current.bestRotationRadS ?? -Infinity,
            event.rotationRadS
          );
        }
      }
      continue;
    }

    candidates.push({
      at: event.at,
      lastSignalAt: event.at,
      sources: [event.source],
      bestRiseDb: event.source === 'audio' ? event.rise : null,
      bestMagnitude: event.source === 'wrist' ? event.magnitude : null,
      stillnessMs: event.source === 'wrist' ? (event.stillnessMs ?? null) : null,
      bestRotationRadS: event.source === 'wrist' ? (event.rotationRadS ?? null) : null,
      tapNoise: uiTapTimes.some((tap) => Math.abs(event.at - tap) <= TAP_NOISE_MS),
    });
  }

  return candidates.map(({ lastSignalAt: _lastSignalAt, ...candidate }) => candidate);
};

export type PuttVerdict = 'correct' | 'wrong';

export type PuttEstimateTally = {
  correct: number;
  wrong: number;
  /** Genuine candidates still waiting for a verdict. */
  pending: number;
  /** Real putts the detector never noticed (the "missed" button). */
  missed: number;
};

export const tallyPuttEstimates = (
  candidates: readonly PuttCandidate[],
  verdicts: Readonly<Record<number, PuttVerdict>>,
  missed: number
): PuttEstimateTally => {
  const genuine = candidates.filter((candidate) => !candidate.tapNoise);
  const correct = genuine.filter((candidate) => verdicts[candidate.at] === 'correct').length;
  const wrong = genuine.filter((candidate) => verdicts[candidate.at] === 'wrong').length;
  return {
    correct,
    wrong,
    pending: genuine.length - correct - wrong,
    missed,
  };
};

/** The oldest genuine candidate awaiting judgment, if any. */
export const oldestPendingCandidate = (
  candidates: readonly PuttCandidate[],
  verdicts: Readonly<Record<number, PuttVerdict>>
): PuttCandidate | null =>
  candidates.find((candidate) => !candidate.tapNoise && verdicts[candidate.at] === undefined) ??
  null;
